import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';
import {
  DocumentsIcon,
  TaxReturnIcon,
  TaxDocumentIcon,
  HealthcareDirectiveIcon,
  HealthcarePowerOfAttorneyIcon,
  LastWillIcon,
  LivingTrustIcon,
  OtherLegalDocumentsIcon,
} from '../ui/Icons';

interface PersonalDocumentItem {
  id: string;
  category: 'documents';
  name: string;
  description: string;
  location: string;
  handlingInstructions: string;
  notes: string;
  createdAt: string;
  documents?: AppDocument[];
}

interface TaxDocument {
  id: string;
  type: 'federal' | 'state' | 'property' | 'other';
  year: string;
  description: string;
  status: 'filed' | 'pending' | 'extension';
  amount?: number;
  documents: AppDocument[];
  notes?: string;
}

interface LegalDocument {
  id: string;
  type: 'will' | 'trust' | 'healthcare_power_of_attorney' | 'healthcare_directive' | 'other';
  name: string;
  description?: string;
  uploadDate: string;
  lastModified?: string;
  notes?: string;
  documents: AppDocument[];
}

type DocumentCategoryKey = 'important_documents' | 'tax_documents' | 'legal_documents';
type DocumentFilterKey = 'all' | DocumentCategoryKey;

interface DocumentOverviewItem {
  id: string;
  categoryKey: DocumentCategoryKey;
  categoryLabel: string;
  typeLabel: string;
  title: string;
  subtitle?: string;
  details: string[];
  notes?: string;
  documentsCount: number;
  raw: PersonalDocumentItem | TaxDocument | LegalDocument;
}

const DOCUMENT_CATEGORY_OPTIONS = [
  { value: 'important_documents', label: 'Important Documents' },
  { value: 'tax_documents', label: 'Tax Documents' },
  { value: 'legal_documents', label: 'Legal Documents' },
] as const;

const TAX_TYPES = [
  { value: 'federal', label: 'Federal Tax Returns' },
  { value: 'state', label: 'State Tax Returns' },
  { value: 'property', label: 'Property Tax Records' },
  { value: 'other', label: 'Other Tax Documents' },
];

const LEGAL_TYPES = [
  { value: 'will', label: 'Last Will and Testament' },
  { value: 'trust', label: 'Living Trust' },
  { value: 'healthcare_power_of_attorney', label: 'Healthcare Power of Attorney' },
  { value: 'healthcare_directive', label: 'Healthcare Directive' },
  { value: 'other', label: 'Other Legal Documents' },
];

const findLabel = (options: { value: string; label: string }[], value?: string) =>
  options.find((o) => o.value === value)?.label || value || '';

const parseNumber = (value: string) => {
  const cleaned = value.replace(/,/g, '').trim();
  return cleaned ? parseFloat(cleaned) : undefined;
};

/* Simple icon for the categories stat card */
const CategoriesIcon = ({ className = 'w-6 h-6 text-cyan-700' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="3" y="4" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="14" y="4" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" strokeWidth="2" />
  </svg>
);

/* Read local backup arrays when session-backed values are missing */
const loadBackupArray = <T,>(key: string): T[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

/* Support older legal document shape that stored only fileUrl */
const normalizeLegacyLegalDocuments = (docs: unknown[] = []): LegalDocument[] =>
  docs.map((doc) => {
    const normalizedDocuments =
      Array.isArray(doc.documents) && doc.documents.length > 0
        ? normalizeDocuments(doc.documents)
        : [];

    return {
      id: doc.id,
      type: doc.type,
      name: doc.name,
      description: doc.description,
      uploadDate: doc.uploadDate,
      lastModified: doc.lastModified,
      notes: doc.notes,
      documents: normalizedDocuments,
    } as LegalDocument;
  });

/* Match icons to the document category and subtype shown in the list */
const getDocumentIcon = (
  categoryKey: DocumentCategoryKey,
  type?: string,
  className = 'w-6 h-6 text-cyan-700'
) => {
  if (categoryKey === 'important_documents') {
    return <DocumentsIcon className={className} />;
  }

  if (categoryKey === 'tax_documents') {
    if (type === 'federal') return <TaxReturnIcon className={className} />;
    return <TaxDocumentIcon className={className} />;
  }

  if (categoryKey === 'legal_documents') {
    switch (type) {
      case 'will':
        return <LastWillIcon className={className} />;
      case 'trust':
        return <LivingTrustIcon className={className} />;
      case 'healthcare_power_of_attorney':
        return <HealthcarePowerOfAttorneyIcon className={className} />;
      case 'healthcare_directive':
        return <HealthcareDirectiveIcon className={className} />;
      default:
        return <OtherLegalDocumentsIcon className={className} />;
    }
  }

  return <DocumentsIcon className={className} />;
};

/* Pull attached files from any overview item in one consistent shape */
const getAttachedDocuments = (item: DocumentOverviewItem): AppDocument[] => {
  switch (item.categoryKey) {
    case 'important_documents': {
      const raw = item.raw as PersonalDocumentItem;
      return normalizeDocuments(raw.documents || []);
    }
    case 'tax_documents': {
      const raw = item.raw as TaxDocument;
      return normalizeDocuments(raw.documents || []);
    }
    case 'legal_documents': {
      const raw = item.raw as LegalDocument;
      return normalizeDocuments(raw.documents || []);
    }
    default:
      return [];
  }
};

const openDocument = (doc: AppDocument) => {
  if (!doc.id) return;
  documentService.open(doc.id);
};

const downloadDocument = async (doc: AppDocument) => {
  if (!doc.id) return;

  try {
    await documentService.download(doc.id, doc.name);
  } catch (error) {
    console.error('[Frame144] Failed to download document:', error);
    alert('Failed to download document.');
  }
};

const Frame144: React.FC = () => {
  const { session, updateTaskProgress } = useSessionPersistence();

  const [loading, setLoading] = useState(true);

  /* Separate state per category so persistence stays predictable */
  const [importantDocuments, setImportantDocuments] = useState<PersonalDocumentItem[]>([]);
  const [taxDocuments, setTaxDocuments] = useState<TaxDocument[]>([]);
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([]);

  /* Filter for the combined Ã¢â‚¬Å“All DocumentsÃ¢â‚¬Â list */
  const [selectedFilter, setSelectedFilter] = useState<DocumentFilterKey>('all');

  /* Modal and submit state */
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  /* Shared modal form state */
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategoryKey | ''>('');
  const [selectedType, setSelectedType] = useState('');

  /* Important document fields */
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [handlingInstructions, setHandlingInstructions] = useState('');
  const [notes, setNotes] = useState('');

  /* Tax document fields */
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [status, setStatus] = useState<TaxDocument['status']>('filed');
  const [amount, setAmount] = useState('');

  /* Files currently selected in the modal */
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  /* Hydrate from session first, then fallback to local backup */
  useEffect(() => {
    const personalAnswers = session?.taskProgress?.personal?.answers || {};
    const taxAnswers = session?.taskProgress?.tax_documents?.answers || {};
    const legalAnswers = session?.taskProgress?.legal_documents?.answers || {};

    const sessionPersonalItems = (personalAnswers.personalItems as PersonalDocumentItem[]) || [];
    const filteredImportantDocs = sessionPersonalItems
      .filter((item) => item.category === 'documents')
      .map((item) => ({
        ...item,
        documents: normalizeDocuments(item.documents || []),
      }));

    setImportantDocuments(
      filteredImportantDocs.length
        ? filteredImportantDocs
        : loadBackupArray<PersonalDocumentItem>('personal-items-backup')
            .filter((item) => item.category === 'documents')
            .map((item) => ({
              ...item,
              documents: normalizeDocuments(item.documents || []),
            }))
    );

    setTaxDocuments(
      ((taxAnswers.documents as TaxDocument[]) || []).length
        ? ((taxAnswers.documents as TaxDocument[]) || []).map((doc) => ({
            ...doc,
            documents: normalizeDocuments(doc.documents || []),
          }))
        : loadBackupArray<TaxDocument>('tax-documents-backup').map((doc) => ({
            ...doc,
            documents: normalizeDocuments(doc.documents || []),
          }))
    );

    setLegalDocuments(
      ((legalAnswers.documents as unknown[]) || []).length
        ? normalizeLegacyLegalDocuments((legalAnswers.documents as unknown[]) || [])
        : normalizeLegacyLegalDocuments(loadBackupArray<Record<string, unknown>>('legal-documents-backup'))
    );

    setLoading(false);
  }, [
    session?.taskProgress?.personal,
    session?.taskProgress?.tax_documents,
    session?.taskProgress?.legal_documents,
  ]);

  /* Clear modal form state before create or edit flows */
  const resetForm = () => {
    setEditingId(null);
    setSelectedCategory('');
    setSelectedType('');

    setName('');
    setDescription('');
    setLocation('');
    setHandlingInstructions('');
    setNotes('');

    setYear(new Date().getFullYear().toString());
    setStatus('filed');
    setAmount('');

    setDocuments([]);
    setIsUploadingDocuments(false);
  };

  /* Persist important documents while preserving other personal items */
  const persistImportantDocuments = async (updatedItems: PersonalDocumentItem[]) => {
    setImportantDocuments(updatedItems);

    try {
      const currentPersonal = ((session?.taskProgress?.personal?.answers?.personalItems as unknown[]) || []);
      const existingNonDocuments = currentPersonal.filter((item) => item.category !== 'documents');
      const merged = [...existingNonDocuments, ...updatedItems];

      localStorage.setItem('personal-items-backup', JSON.stringify(merged));
      await updateTaskProgress('personal', { personalItems: merged });
    } catch (error) {
      console.error('[Frame144] Failed to persist important documents:', error);
      throw error;
    }
  };

  /* Persist tax documents to local backup and session */
  const persistTaxDocuments = async (updatedItems: TaxDocument[]) => {
    setTaxDocuments(updatedItems);

    try {
      localStorage.setItem('tax-documents-backup', JSON.stringify(updatedItems));
      await updateTaskProgress('tax_documents', { documents: updatedItems }, 'in-progress');
    } catch (error) {
      console.error('[Frame144] Failed to persist tax documents:', error);
      throw error;
    }
  };

  /* Persist legal documents to local backup and session */
  const persistLegalDocuments = async (updatedItems: LegalDocument[]) => {
    setLegalDocuments(updatedItems);

    try {
      localStorage.setItem('legal-documents-backup', JSON.stringify(updatedItems));
      await updateTaskProgress('legal_documents', { documents: updatedItems });
    } catch (error) {
      console.error('[Frame144] Failed to persist legal documents:', error);
      throw error;
    }
  };

  /* Flatten all categories into one list for rendering and filtering */
  const overviewItems: DocumentOverviewItem[] = useMemo(() => {
    const important: DocumentOverviewItem[] = importantDocuments.map((doc) => ({
      id: doc.id,
      categoryKey: 'important_documents',
      categoryLabel: 'Important Documents',
      typeLabel: 'Important Document',
      title: doc.name,
      subtitle: doc.location || 'No location listed',
      details: [
        doc.description ? `Description: ${doc.description}` : '',
        doc.handlingInstructions ? `Handling: ${doc.handlingInstructions}` : '',
      ].filter(Boolean),
      notes: doc.notes,
      documentsCount: doc.documents?.length || 0,
      raw: doc,
    }));

    const tax: DocumentOverviewItem[] = taxDocuments.map((doc) => ({
      id: doc.id,
      categoryKey: 'tax_documents',
      categoryLabel: 'Tax Documents',
      typeLabel: findLabel(TAX_TYPES, doc.type),
      title: `${doc.description} (${doc.year})`,
      subtitle: `Status: ${doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}`,
      details: [
        doc.amount !== undefined ? `Amount: $${doc.amount.toLocaleString()}` : '',
      ].filter(Boolean),
      notes: doc.notes,
      documentsCount: doc.documents?.length || 0,
      raw: doc,
    }));

    const legal: DocumentOverviewItem[] = legalDocuments.map((doc) => ({
      id: doc.id,
      categoryKey: 'legal_documents',
      categoryLabel: 'Legal Documents',
      typeLabel: findLabel(LEGAL_TYPES, doc.type),
      title: doc.name,
      subtitle: doc.uploadDate ? `Uploaded ${new Date(doc.uploadDate).toLocaleDateString()}` : '',
      details: [],
      notes: doc.notes || doc.description,
      documentsCount: doc.documents?.length || 0,
      raw: doc,
    }));

    return [...important, ...tax, ...legal];
  }, [importantDocuments, taxDocuments, legalDocuments]);

  /* Apply selected filter to the flattened list */
  const filteredItems = useMemo(() => {
    if (selectedFilter === 'all') return overviewItems;
    return overviewItems.filter((item) => item.categoryKey === selectedFilter);
  }, [overviewItems, selectedFilter]);

  const totalDocuments = overviewItems.length;
  const totalCategories = [
    importantDocuments.length > 0,
    taxDocuments.length > 0,
    legalDocuments.length > 0,
  ].filter(Boolean).length;

  /* Stats shown above the document list */
  const statCards = [
    {
      label: 'Total Documents',
      value: totalDocuments,
      icon: <DocumentsIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'Categories',
      value: totalCategories,
      icon: <CategoriesIcon className="w-6 h-6 text-cyan-700" />,
    },
  ];

  /* Open modal in create mode */
  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
  };

  /* Load an existing item into the modal for editing */
  const handleEditItem = (item: DocumentOverviewItem) => {
    resetForm();
    setEditingId(item.id);
    setSelectedCategory(item.categoryKey);

    switch (item.categoryKey) {
      case 'important_documents': {
        const raw = item.raw as PersonalDocumentItem;
        setName(raw.name);
        setDescription(raw.description || '');
        setLocation(raw.location || '');
        setHandlingInstructions(raw.handlingInstructions || '');
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'tax_documents': {
        const raw = item.raw as TaxDocument;
        setSelectedType(raw.type);
        setYear(raw.year);
        setDescription(raw.description);
        setStatus(raw.status);
        setAmount(raw.amount?.toString() || '');
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'legal_documents': {
        const raw = item.raw as LegalDocument;
        setSelectedType(raw.type);
        setNotes(raw.notes || raw.description || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      default:
        break;
    }

    setShowModal(true);
  };

  /* Delete an item from the correct category store */
  const handleDeleteItem = async (item: DocumentOverviewItem) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const attachedDocuments = getAttachedDocuments(item);
      for (const doc of attachedDocuments) {
        try {
          await documentService.delete(doc.id);
        } catch (error) {
          console.error('[Frame144] Failed to delete document while deleting item:', error);
        }
      }

      switch (item.categoryKey) {
        case 'important_documents':
          await persistImportantDocuments(
            importantDocuments.filter((doc) => doc.id !== item.id)
          );
          break;
        case 'tax_documents':
          await persistTaxDocuments(
            taxDocuments.filter((doc) => doc.id !== item.id)
          );
          break;
        case 'legal_documents':
          await persistLegalDocuments(
            legalDocuments.filter((doc) => doc.id !== item.id)
          );
          break;
      }
    } catch (error) {
      console.error('[Frame144] Failed to delete document:', error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const handleRemoveAttachedDocument = async (
    item: DocumentOverviewItem,
    documentIndex: number
  ) => {
    try {
      const attachedDocuments = getAttachedDocuments(item);
      const docToRemove = attachedDocuments[documentIndex];
      if (!docToRemove) return;

      await documentService.delete(docToRemove.id);

      switch (item.categoryKey) {
        case 'important_documents': {
          const raw = item.raw as PersonalDocumentItem;

          const updatedItem: PersonalDocumentItem = {
            ...raw,
            documents: normalizeDocuments(raw.documents || []).filter((_, index) => index !== documentIndex),
          };

          const updatedItems = importantDocuments.map((doc) =>
            doc.id === raw.id ? updatedItem : doc
          );

          await persistImportantDocuments(updatedItems);
          break;
        }

        case 'tax_documents': {
          const raw = item.raw as TaxDocument;

          const updatedItem: TaxDocument = {
            ...raw,
            documents: normalizeDocuments(raw.documents || []).filter((_, index) => index !== documentIndex),
          };

          const updatedItems = taxDocuments.map((doc) =>
            doc.id === raw.id ? updatedItem : doc
          );

          await persistTaxDocuments(updatedItems);
          break;
        }

        case 'legal_documents': {
          const raw = item.raw as LegalDocument;

          const updatedItem: LegalDocument = {
            ...raw,
            documents: normalizeDocuments(raw.documents || []).filter((_, index) => index !== documentIndex),
          };

          const updatedItems = legalDocuments.map((doc) =>
            doc.id === raw.id ? updatedItem : doc
          );

          await persistLegalDocuments(updatedItems);
          break;
        }

        default:
          break;
      }
    } catch (error) {
      console.error('[Frame144] Failed to remove attached document:', error);
      alert('Failed to remove attached document. Please try again.');
    }
  };

  /* Validate form, upload files, and persist into the chosen category */
  const handleSave = async () => {
    if (submitting || isUploadingDocuments) return;

    if (!selectedCategory) {
      alert('Please select a document category');
      return;
    }

    if (selectedCategory === 'important_documents' && !name.trim()) {
      alert('Please enter a document name');
      return;
    }

    if (
      selectedCategory === 'tax_documents' &&
      (!selectedType || !year.trim() || !description.trim() || documents.length === 0)
    ) {
      alert('Please fill in all required tax document fields');
      return;
    }

    if (selectedCategory === 'legal_documents' && (!selectedType || documents.length === 0)) {
      alert('Please select a legal document type and upload a file');
      return;
    }

    setSubmitting(true);

    try {
      switch (selectedCategory) {
        case 'important_documents': {
          const newItem: PersonalDocumentItem = {
            id: editingId || Date.now().toString(),
            category: 'documents',
            name: name.trim(),
            description: description.trim(),
            location: location.trim(),
            handlingInstructions: handlingInstructions.trim(),
            notes: notes.trim(),
            createdAt: new Date().toISOString(),
            documents,
          };

          const updated = editingId
            ? importantDocuments.map((doc) => (doc.id === editingId ? newItem : doc))
            : [...importantDocuments, newItem];

          await persistImportantDocuments(updated);
          break;
        }

        case 'tax_documents': {
          const newItem: TaxDocument = {
            id: editingId || Date.now().toString(),
            type: selectedType as TaxDocument['type'],
            year: year.trim(),
            description: description.trim(),
            status,
            amount: parseNumber(amount),
            documents,
            notes: notes.trim() || undefined,
          };

          const updated = editingId
            ? taxDocuments.map((doc) => (doc.id === editingId ? newItem : doc))
            : [...taxDocuments, newItem];

          await persistTaxDocuments(updated);
          break;
        }

        case 'legal_documents': {
          const firstDoc = documents[0];

          const newItem: LegalDocument = {
            id: editingId || Date.now().toString(),
            type: selectedType as LegalDocument['type'],
            name: firstDoc?.name || 'Legal Document',
            uploadDate: firstDoc?.createdAt || new Date().toISOString(),
            notes: notes.trim() || undefined,
            description: notes.trim() || undefined,
            documents,
          };

          const updated = editingId
            ? legalDocuments.map((doc) => (doc.id === editingId ? newItem : doc))
            : [...legalDocuments, newItem];

          await persistLegalDocuments(updated);
          break;
        }
      }

      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('[Frame144] Failed to save document:', error);
      alert('Failed to save document. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame144] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px]">
          {/* Header and primary upload action */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <ResponsiveHeader title="Your Paper Trail" />
            </div>
            <div className="shrink-0 mb-4 sm:mb-0">
              <button
                onClick={handleOpenModal}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-700 text-white text-sm font-medium rounded-md hover:bg-cyan-800 transition-colors whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15V4" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 8l4-4 4 4" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
                </svg>
                Upload Document
              </button>
            </div>
          </div>

          {/* Intro copy for the page */}
          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            Upload or record the location of important documents such as wills, tax returns, and essential records so they can be found when needed most.
          </p>

          {/* Summary cards */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-md border border-stone-200 p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0">{card.icon}</div>

                  <div className="flex flex-col">
                    <div className="font-sans text-base text-stone-600">{card.label}</div>
                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4">
                      {loading ? 'Ã¢â‚¬â€' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Combined list across all categories */}
          <div className="mt-7">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-3">
              <div className="flex items-center gap-3">
                <h2 className="font-sans text-xl font-medium text-stone-800">
                  All Documents
                </h2>
              </div>

              <div className="shrink-0">
                <select
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value as DocumentFilterKey)}
                  className="min-w-[220px] px-3 py-2 border border-stone-300 rounded-md bg-white text-sm text-stone-800"
                >
                  <option value="all">All ({overviewItems.length})</option>
                  <option value="important_documents">
                    Important Documents ({importantDocuments.length})
                  </option>
                  <option value="tax_documents">
                    Tax Documents ({taxDocuments.length})
                  </option>
                  <option value="legal_documents">
                    Legal Documents ({legalDocuments.length})
                  </option>
                </select>
              </div>
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading documents...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">
                    No documents found for this filter yet.
                  </p>
                </div>
              ) : (
                filteredItems.map((item) => {
                  const attachedDocuments = getAttachedDocuments(item);
                  const itemType =
                    item.categoryKey === 'tax_documents' || item.categoryKey === 'legal_documents'
                      ? (item.raw as TaxDocument | LegalDocument).type
                      : undefined;

                  return (
                    <div
                      key={`${item.categoryKey}-${item.id}`}
                      className="flex flex-col sm:flex-row sm:items-start gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 overflow-hidden max-w-full"
                    >
                      {/* Main row content */}
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0">
                        <div className="shrink-0 mt-0.5">
                          {getDocumentIcon(item.categoryKey, itemType, 'w-6 h-6 text-cyan-700')}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-sans text-lg font-medium text-stone-800 break-words min-w-0">
                              {item.title}
                            </div>
                            <span className="inline-flex items-center rounded-full bg-stone-200 text-stone-800 px-2.5 py-1 text-xs font-medium">
                              {item.categoryLabel}
                            </span>
                          </div>

                          <div className="font-sans text-base text-stone-600 mt-1">
                            {item.typeLabel}
                          </div>

                          {item.subtitle && (
                            <div className="font-sans text-base text-stone-600 mt-1">
                              {item.subtitle}
                            </div>
                          )}

                          {item.details.length > 0 && (
                            <div className="flex flex-wrap gap-x-5 gap-y-1 font-sans text-sm sm:text-base text-stone-600 mt-1 break-words">
                              {item.details.map((detail, index) => (
                                <span key={index}>{detail}</span>
                              ))}
                            </div>
                          )}

                          {item.notes && (
                            <div className="font-sans text-sm text-stone-500 mt-2">
                              {item.notes}
                            </div>
                          )}

                          {attachedDocuments.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <div className="font-sans text-sm text-cyan-700">
                                {attachedDocuments.length} document(s) attached
                              </div>

                              <div className="flex flex-col gap-2">
                                {attachedDocuments.map((doc, index) => (
                                  <div
                                    key={`${item.id}-file-${doc.id}-${index}`}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-2 sm:px-3 py-2 overflow-hidden max-w-full"
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openDocument(doc)}
                                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-sm font-medium text-stone-800 hover:text-cyan-700 transition-colors text-left"
                                    >
                                      {getDocumentIcon(item.categoryKey, itemType, 'w-4 h-4 text-cyan-700 shrink-0 mt-0.5')}
                                      <span className="truncate min-w-0" title={doc.name}>
                                        {doc.name}
                                      </span>
                                    </button>

                                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => downloadDocument(doc)}
                                        className="text-cyan-600 hover:text-cyan-800 text-xs"
                                        aria-label={`Download ${doc.name}`}
                                        title="Download document"
                                      >
                                        Download
                                      </button>

                                      <button
                                        type="button"
                                        onClick={() => handleRemoveAttachedDocument(item, index)}
                                        className="text-red-500 hover:text-red-700 text-xs"
                                        aria-label={`Remove ${doc.name}`}
                                        title="Remove document"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Row actions */}
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => handleEditItem(item)}
                          className="text-cyan-600 hover:text-cyan-800 px-3 py-1 rounded border border-cyan-400 hover:border-cyan-700 font-sans text-sm font-medium whitespace-nowrap transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item)}
                          className="text-red-600 hover:text-red-800 px-3 py-1 rounded border border-red-300 hover:border-red-700 font-sans text-sm font-medium whitespace-nowrap transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>

      {/* Shared modal for create and edit across all document categories */}
      <Modal
        title={editingId ? 'Edit Document' : 'Upload Document'}
        open={showModal}
        onOk={handleSave}
        onCancel={() => {
          setShowModal(false);
          resetForm();
        }}
        okText={submitting
          ? editingId
            ? 'Updating...'
            : 'Uploading...'
          : editingId
            ? 'Update Document'
            : 'Upload Document'}
        cancelText="Cancel"
      >
        <div className="space-y-6">
          {/* Category selector drives which fields appear below */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Document Category *
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value as DocumentCategoryKey);
                setSelectedType('');
                setDocuments([]);
              }}
              disabled={!!editingId}
              className="w-full px-3 py-2 border border-stone-300 rounded-md disabled:bg-stone-100"
            >
              <option value="">Select category</option>
              {DOCUMENT_CATEGORY_OPTIONS.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tax-specific form fields */}
          {selectedCategory === 'tax_documents' && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Tax Type *
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                >
                  <option value="">Select type</option>
                  {TAX_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Tax Year *
                </label>
                <input
                  type="text"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="YYYY"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Description *
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="e.g., Federal Income Tax Return 2024"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Status *
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TaxDocument['status'])}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                >
                  <option value="filed">Filed</option>
                  <option value="pending">Pending</option>
                  <option value="extension">Extension Filed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Amount
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="0.00"
                />
              </div>
            </>
          )}

          {/* Legal-specific form fields */}
          {selectedCategory === 'legal_documents' && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Legal Document Type *
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
              >
                <option value="">Select type</option>
                {LEGAL_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Important document-specific form fields */}
          {selectedCategory === 'important_documents' && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Document Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="e.g., Birth Certificate"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Where is it stored?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Handling Instructions
                </label>
                <textarea
                  value={handlingInstructions}
                  onChange={(e) => setHandlingInstructions(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Any special handling instructions"
                />
              </div>
            </>
          )}

          {/* Shared notes field */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
                placeholder="Any additional notes"
              />
            </div>
          )}

          {/* Shared upload area */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Upload Documents {selectedCategory !== 'important_documents' ? '*' : ''}
              </label>
              <FileUpload
                onUpload={async (files) => {
                  if (!files.length) return;

                  setIsUploadingDocuments(true);

                  try {
                    const uploadedDocs = await Promise.all(
                      files.map((file) => documentService.upload(file))
                    );

                    setDocuments((prev) => [...prev, ...uploadedDocs]);
                  } catch (error) {
                    console.error('[Frame144] Failed to upload document(s):', error);
                    alert('Failed to upload one or more documents.');
                  } finally {
                    setIsUploadingDocuments(false);
                  }
                }}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                multiple={selectedCategory === 'important_documents' || selectedCategory === 'tax_documents'}
              />

              {isUploadingDocuments && (
                <div className="mt-3 text-sm text-stone-500">
                  Uploading documents...
                </div>
              )}

              {documents.length > 0 && (
                <div className="mt-3 space-y-2">
                  {documents.map((doc, index) => (
                    <div
                      key={`${doc.id}-${index}`}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => openDocument(doc)}
                        className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-sm font-medium text-stone-800 hover:text-cyan-700 transition-colors text-left"
                      >
                        {getDocumentIcon(
                          selectedCategory,
                          selectedCategory === 'tax_documents' || selectedCategory === 'legal_documents'
                            ? selectedType
                            : undefined,
                          'w-4 h-4 text-cyan-700 shrink-0 mt-0.5'
                        )}
                        <span className="text-sm text-stone-600 truncate min-w-0" title={doc.name}>
                          {doc.name}
                        </span>
                      </button>

                      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                        <button
                          type="button"
                          onClick={() => downloadDocument(doc)}
                          className="text-cyan-600 hover:text-cyan-800 text-xs"
                        >
                          Download
                        </button>

                        <button
                          onClick={() => removeDocumentFromForm(index)}
                          className="text-red-600 hover:text-red-700 text-xs shrink-0"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </ResponsiveContainer>
  );
};

export default Frame144;